/**
 * Pipeline glance panel for the home view per spec section 15.10 and
 * design README section 5.1.
 *
 * Three large numbers (open opportunities / weighted pipeline /
 * stalled) above a horizontal stage bar.
 *
 * The 30-day weighted-pipeline sparkline from the design is deferred
 * to M13 polish per the autonomous goal directive — it needs a
 * daily-stats-rollup admin job which is M11 work.
 */

import type { WirePipelineGlance } from '@/lib/api/sales';
import { cn } from '@/lib/utils';

interface PipelineGlanceProps {
  glance: WirePipelineGlance;
}

export function PipelineGlance({ glance }: PipelineGlanceProps) {
  // Record<string, number> per the wire shape but each value could be
  // undefined if the backend returns a partial; default to zero so the
  // segment widths stay valid.
  const newCount = glance.byStage['NEW'] ?? 0;
  const activeCount = glance.byStage['ACTIVE'] ?? 0;
  const awaitingCount = glance.byStage['AWAITING'] ?? 0;
  const total = newCount + activeCount + awaitingCount || 1;
  return (
    <section className="border border-mx-grey-800 bg-mx-card p-mx-4">
      <p className="mono-label text-mx-grey-mid">
        <span className="num">02</span>
        Pipeline at a glance
      </p>
      <div className="mt-mx-3 grid grid-cols-3 gap-mx-4">
        <Metric label="OPEN OPPORTUNITIES" value={glance.openCount.toString()} />
        <Metric label="WEIGHTED" value={formatCurrency(glance.weightedValue)} />
        <Metric
          label="STALLED"
          value={glance.stalledCount.toString()}
          tone={glance.stalledCount > 0 ? 'warn' : 'default'}
        />
      </div>

      <div className="mt-mx-4">
        <p className="mono-label text-mx-grey-mid">BY STAGE</p>
        <div className="mt-mx-2 flex h-3 overflow-hidden border border-mx-grey-800">
          <Segment ratio={newCount / total} tone="cool" label={`NEW · ${newCount}`} />
          <Segment ratio={activeCount / total} tone="warm" label={`ACTIVE · ${activeCount}`} />
          <Segment
            ratio={awaitingCount / total}
            tone="orange"
            label={`AWAITING · ${awaitingCount}`}
          />
        </div>
        <div className="mt-mx-2 flex justify-between text-mx-mono-sm text-mx-grey-mid">
          <span>NEW {newCount}</span>
          <span>ACTIVE {activeCount}</span>
          <span>AWAITING {awaitingCount}</span>
        </div>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div>
      <p className="mono-label text-mx-grey-mid">{label}</p>
      <p
        className={cn(
          'tnum font-display text-mx-h1 font-semibold leading-tight',
          tone === 'warn' ? 'text-mx-orange' : 'text-mx-grey-300',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function Segment({
  ratio,
  tone,
  label,
}: {
  ratio: number;
  tone: 'cool' | 'warm' | 'orange';
  label: string;
}) {
  const width = `${Math.max(0, ratio * 100)}%`;
  const className =
    tone === 'cool' ? 'bg-mx-grey-mid' : tone === 'warm' ? 'bg-mx-grey-500' : 'bg-mx-orange';
  return <div role="presentation" aria-label={label} className={className} style={{ width }} />;
}

function formatCurrency(num: number): string {
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}
